import {
  BMComponentsType,
  BMElevationType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Animated,
  FlatList,
  LayoutChangeEvent,
  Platform,
  Pressable,
  StyleSheet,
} from "react-native";

import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { ErrorSection } from "../../Error";
import { BMDropdownOptionRow, buildOptionRows } from "../groupRows";
import {
  MAX_HEIGHT_OPTIONS_CONTAINER,
  useAnimatedDropdownHeight,
  useGetValue,
} from "../hooks";
import {
  BMDropdownDesktopOptionsProps,
  BMDropdownOptionsValue,
} from "../types";

import { appendTestId } from "../../../utils";
import DotLoader from "../../DotLoader";
import BMView from "../../View/BMView";
import BMDropdownGroupSelectRow from "./BMDropdownGroupSelectRow";
import BMDropdownNotFoundOptions from "./BMDropdownNotFoundOptions";
import BMDropdownOptionItem from "./BMDropdownOptionItem";

const ITEM_HEIGHT = 48;

export default function BMDropdownDesktopOptions<T>({
  show,
  query,
  option,
  isRadio,
  selected,
  isMultiple,
  wrapperRef,
  optionsSection,
  searchable,
  onChange,
  onClose,
  getOptionValue,
  renderSelectionItem,
  testID,
  error,
  maxHeight = MAX_HEIGHT_OPTIONS_CONTAINER,
}: BMDropdownDesktopOptionsProps<T>) {
  const { noResultCaption, NoResultFooterComponent } =
    typeof searchable !== "boolean" && searchable
      ? searchable
      : { noResultCaption: undefined, NoResultFooterComponent: null };

  const { BMComponents, BMElevation } = useBMTheme();
  const styles = useMemo(
    () =>
      createStyles(BMComponents, BMElevation, show, Boolean(error), maxHeight),
    [BMComponents, BMElevation, show, error, maxHeight]
  );
  const { CorporateGeneralErrorMessage: cr } = useMobileContentResource();

  const [contentHeight, setContentHeight] = useState(0);
  const { height: animatedHeight, isCollapsed } = useAnimatedDropdownHeight(
    show,
    contentHeight,
    maxHeight
  );
  const getValue = useGetValue(getOptionValue);

  const filteredOptions = useMemo(() => {
    if (!searchable) return option.items;
    return option.items.filter((item) =>
      item.label.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, option.items, searchable]);

  const rows = useMemo(
    () => buildOptionRows(option.items, filteredOptions),
    [option.items, filteredOptions]
  );

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setContentHeight(e.nativeEvent.layout.height);
  }, []);

  /**
   * `selected` is an array when isMultiple, a single value otherwise.
   * Normalize once so every downstream check below is unconditional.
   */
  const resolvedSelectedItems = useMemo(
    () => (Array.isArray(selected) ? selected : selected ? [selected] : []),
    [selected]
  );

  const isItemSelected = useCallback(
    (item: BMDropdownOptionsValue<T>) =>
      resolvedSelectedItems.some(
        (v) => getValue(v.value) === getValue(item.value)
      ),
    [resolvedSelectedItems, getValue]
  );

  const handleItemPress = useCallback(
    (item: BMDropdownOptionsValue<T>) => {
      if (!isMultiple) {
        onChange(item);
        return;
      }
      const next = isItemSelected(item)
        ? resolvedSelectedItems.filter(
            (v) => getValue(v.value) !== getValue(item.value)
          )
        : [...resolvedSelectedItems, item];
      onChange(next);
    },
    [isMultiple, isItemSelected, resolvedSelectedItems, getValue, onChange]
  );

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (
        wrapperRef.current instanceof HTMLElement &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        if (isMultiple) {
          onClose?.();
        } else {
          onChange(resolvedSelectedItems[0] ?? null);
        }
      }
    },
    [wrapperRef, isMultiple, onClose, onChange, resolvedSelectedItems]
  );

  const renderRow = useCallback(
    ({ item: row, index }: { item: BMDropdownOptionRow<T>; index: number }) => {
      if (row.type === "group") {
        const isSelected = row.items.every(isItemSelected);
        return (
          <BMView
            style={
              !error && isSelected
                ? {
                    backgroundColor:
                      BMComponents.BMStyleSurfaceBgcolorHighlight,
                  }
                : {}
            }
          >
            <BMDropdownGroupSelectRow
              label={row.label}
              items={row.items}
              selectedItems={resolvedSelectedItems}
              getValue={getValue}
              onPress={onChange}
            />
          </BMView>
        );
      }
      const isSelected = isItemSelected(row.item);
      return (
        <BMView
          style={[
            !error && isSelected
              ? { backgroundColor: BMComponents.BMStyleSurfaceBgcolorHighlight }
              : {},
            row.indent ? styles.groupIndent : undefined,
          ]}
        >
          <Pressable
            onPress={() => handleItemPress(row.item)}
            testID={appendTestId(testID, `option-${index}`)}
          >
            {renderSelectionItem && isSelected ? (
              renderSelectionItem(row.item)
            ) : (
              <BMDropdownOptionItem
                {...row.item}
                query={query}
                isRadio={isRadio}
                isMultiple={isMultiple}
                isSelected={isSelected}
              />
            )}
          </Pressable>
        </BMView>
      );
    },
    [
      isItemSelected,
      error,
      BMComponents,
      styles.groupIndent,
      testID,
      renderSelectionItem,
      query,
      isRadio,
      isMultiple,
      resolvedSelectedItems,
      getValue,
      onChange,
      handleItemPress,
    ]
  );

  useEffect(() => {
    if (Platform.OS !== "web" || !show) return;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      if (Platform.OS !== "web" || !show) return;
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [show, handleOutsideClick]);

  const renderContent = () => {
    if (option.isLoading) {
      return (
        <BMView style={styles.marginVerticalXXL}>
          <DotLoader />
        </BMView>
      );
    }

    if (option.isError) {
      return (
        <BMView justify="start" align="start">
          <ErrorSection
            type="SMALL"
            title={cr.perSectionTitle}
            caption={cr.perSectionDescription}
            button={[
              {
                type: "SECONDARY",
                label: cr.perSectionCTA,
                onPress: option.onClickRetry ?? (() => {}),
              },
              undefined,
            ]}
            style={styles.errorWrapperStyle}
          />
        </BMView>
      );
    }

    if (filteredOptions.length > 0) {
      const selectedIndex = rows.findIndex(
        (row) => row.type === "item" && isItemSelected(row.item)
      );
      return (
        <FlatList
          data={rows}
          renderItem={renderRow}
          showsVerticalScrollIndicator={false}
          ItemSeparatorComponent={() => <BMView style={styles.divider} />}
          getItemLayout={(_, index) => ({
            length: ITEM_HEIGHT,
            offset: ITEM_HEIGHT * index,
            index,
          })}
          keyExtractor={(row) => row.key}
          initialScrollIndex={selectedIndex > 0 ? selectedIndex : undefined}
        />
      );
    }

    return (
      <BMDropdownNotFoundOptions
        query={query}
        noResultCaption={noResultCaption}
        NoResultFooterComponent={
          typeof NoResultFooterComponent === "function"
            ? NoResultFooterComponent
            : undefined
        }
      />
    );
  };

  return (
    <BMView style={styles.wrapper}>
      <BMView style={styles.container}>
        <Animated.View
          style={[
            styles.card,
            optionsSection?.wrapperStyles,
            { height: animatedHeight },
            isCollapsed ? styles.hidden : undefined,
          ]}
        >
          <BMView onLayout={onLayout} style={optionsSection?.contentStyles}>
            {optionsSection?.title || optionsSection?.description ? (
              <BMView spacing="xs" style={styles.optionsSection}>
                {optionsSection.title && (
                  <BMText variant="titleNormal">{optionsSection.title}</BMText>
                )}
                {optionsSection.description && (
                  <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
                    {optionsSection.description}
                  </BMText>
                )}
              </BMView>
            ) : null}
            {renderContent()}
            {typeof error === "string" && (
              <BMText
                variant="uiSmall"
                ink="BMUiInputInkError"
                style={styles.errorCaption}
              >
                {error}
              </BMText>
            )}
          </BMView>
        </Animated.View>
      </BMView>
    </BMView>
  );
}

function createStyles(
  BMComponents: BMComponentsType,
  BMElevation: BMElevationType,
  isOpen: boolean,
  isError: boolean,
  maxHeight: number
) {
  return StyleSheet.create({
    wrapper: { position: "relative" },
    hidden: { borderWidth: 0, opacity: 0 },
    container: {
      zIndex: 99,
      height: "100%",
      width: "100%",
      position: "absolute",
      top: BMPrimitives.BMSize.xs,
    },
    card: {
      ...BMElevation.elevation2,
      overflow: "scroll",
      borderRadius: BMComponents.BMStyleCardRadiusCompact,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      maxHeight,
      ...(isOpen && {
        borderWidth: BMPrimitives.BMBorder.width.thick,
        borderColor: BMComponents.BMStyleCardBordercolor,
      }),
      ...(isError && {
        borderWidth: BMPrimitives.BMBorder.width.thick,
        borderColor: BMComponents.BMStyleLineBordercolorError,
        backgroundColor: BMComponents.BMUiControlBgcolorGroupError,
      }),
      // @ts-expect-error -- web-only CSS property, not in RN's ViewStyle types
      overscrollBehavior: "none",
    },
    divider: {
      borderBottomWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleCardBordercolor,
    },
    groupIndent: { paddingLeft: BMPrimitives.BMSize.lg },
    optionsSection: {
      marginBottom: BMPrimitives.BMSize.md,
    },
    marginVerticalXXL: { marginVertical: BMPrimitives.BMSize.xl2 },
    errorWrapperStyle: {
      minWidth: "auto",
      marginTop: BMPrimitives.BMSize.xs,
      marginBottom: -BMPrimitives.BMSize.md,
      marginHorizontal: BMPrimitives.BMSize.md,
    },
    errorCaption: {
      marginTop: BMPrimitives.BMSize.xs,
      paddingHorizontal: BMPrimitives.BMSize.md,
    },
  });
}
